import { useEffect, useRef, useCallback, RefObject } from 'react';
import { useScrollPositionContext } from '@/context/ScrollPositionContext';

interface UseScrollPositionOptions {
  /** Unique key to identify this scroll container */
  key: string;
  /** Ref to the scrollable element */
  scrollRef: RefObject<HTMLElement>;
  /** Whether to restore scroll position on mount (default: true) */
  restoreOnMount?: boolean;
  /** Delay before restoring scroll (ms) - useful for dynamic content (default: 50) */
  restoreDelay?: number;
  /** Whether scroll preservation is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook to preserve scroll position across component unmount/remount.
 *
 * Usage:
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null);
 * useScrollPosition({ key: 'my-list', scrollRef });
 *
 * return <div ref={scrollRef} className="overflow-y-auto">...</div>;
 * ```
 */
export function useScrollPosition({
  key,
  scrollRef,
  restoreOnMount = true,
  restoreDelay = 50,
  enabled = true,
}: UseScrollPositionOptions) {
  const { getScrollPosition, setScrollPosition } = useScrollPositionContext();
  const hasRestoredRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the last known scroll position so we can save it on unmount even if DOM is gone
  const lastScrollPositionRef = useRef<number>(0);

  // Save scroll position on scroll (debounced)
  const handleScroll = useCallback(() => {
    if (!enabled || !scrollRef.current) return;

    // Always update the last known position immediately
    lastScrollPositionRef.current = scrollRef.current.scrollTop;

    // Debounce saves to context to avoid excessive updates
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (scrollRef.current) {
        setScrollPosition(key, scrollRef.current.scrollTop);
      }
    }, 100);
  }, [key, scrollRef, setScrollPosition, enabled]);

  // Restore scroll position on mount
  useEffect(() => {
    if (!enabled || !restoreOnMount || hasRestoredRef.current) return;

    const restore = () => {
      const savedPosition = getScrollPosition(key);
      if (savedPosition > 0 && scrollRef.current) {
        scrollRef.current.scrollTop = savedPosition;
        hasRestoredRef.current = true;
      }
    };

    // Delay restoration to allow content to render
    const timer = setTimeout(restore, restoreDelay);

    return () => clearTimeout(timer);
  }, [key, scrollRef, getScrollPosition, restoreOnMount, restoreDelay, enabled]);

  // Set up scroll listener
  useEffect(() => {
    if (!enabled) return;

    const element = scrollRef.current;
    if (!element) return;

    // Initialize last known position from current scroll
    lastScrollPositionRef.current = element.scrollTop;

    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
      // Clear pending debounce
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save the last known position on unmount
      // Use the ref value since DOM element may have been removed/reset
      if (lastScrollPositionRef.current > 0) {
        setScrollPosition(key, lastScrollPositionRef.current);
      }
    };
  }, [scrollRef, handleScroll, enabled, key, setScrollPosition]);

  // Reset restoration flag when key changes
  useEffect(() => {
    hasRestoredRef.current = false;
  }, [key]);

  // Immediate save function (no debounce)
  const savePositionNow = useCallback(() => {
    if (!enabled) return;
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    // Use current scroll position if available, otherwise use last known
    const position = scrollRef.current?.scrollTop ?? lastScrollPositionRef.current;
    if (position > 0) {
      setScrollPosition(key, position);
      lastScrollPositionRef.current = position;
    }
  }, [key, scrollRef, setScrollPosition, enabled]);

  return {
    /** Manually save the current scroll position (debounced) */
    savePosition: handleScroll,
    /** Manually save the current scroll position immediately (no debounce) */
    savePositionNow,
    /** Get the saved scroll position (0 if none) */
    getSavedPosition: useCallback(() => {
      return getScrollPosition(key);
    }, [key, getScrollPosition]),
    /** Manually restore the saved scroll position. Returns true if restored, false if no saved position. */
    restorePosition: useCallback(() => {
      const savedPosition = getScrollPosition(key);
      if (savedPosition > 0 && scrollRef.current) {
        scrollRef.current.scrollTop = savedPosition;
        return true;
      }
      return false;
    }, [key, scrollRef, getScrollPosition]),
  };
}

export default useScrollPosition;
